import {
	Autocomplete,
	Box,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import {
	BackgroundColor,
	NeutralColor,
	PrimaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import React, {
	Dispatch,
	SetStateAction,
	useEffect,
	useRef,
	useState,
} from "react";
import { CustomSwitch, LargeSwitch } from "./custom-switch";
import { SimpleTooltip } from "./tooltip";
import { AlertIcon, SearchIcon } from "./svgIcons";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Visibility, VisibilityOff } from "@mui/icons-material";

interface UserInfoTextInputProps {
	label: string;
	description?: string;
	inputType: string;
	value: string;
	setValue: Dispatch<SetStateAction<string>>;
	required?: boolean;
	disabled?: boolean;
}

function UserInfoTextInput(props: UserInfoTextInputProps): JSX.Element {
	const label: React.ReactNode = props.required ? (
		<label>
			{props.label}
			<span style={{ color: "red" }}> * </span>
			{props.description && (
				<div>
					<small className="input-description">{props.description}</small>
				</div>
			)}
		</label>
	) : (
		<label>
			{props.label}
			{props.description && (
				<div>
					<small className="input-description">{props.description}</small>
				</div>
			)}
		</label>
	);

	return (
		<div style={{ display: "flex", columnGap: "20px", alignItems: "center" }}>
			{label}

			<input
				type={props.inputType}
				value={props.value}
				onChange={(e) => {
					let value = e.target.value;
					//formatting
					if (props.inputType === "number") {
						value = value.replace(/^0+/, "") || "0"; // Ensure at least '0' is returned
					}
					props.setValue(value);
				}}
				maxLength={255}
				required={props.required}
				disabled={props.disabled}
			/>
		</div>
	);
}

interface CustomTextInputProps {
	id: string;
	label?: string;
	description?: string;
	placeholder?: string;
	value: string;
	setValue: (value: string) => void;
	isTextArea?: boolean;
	required?: boolean;
	disabled?: boolean;
	type?: string;
	errorMsg?: string;
	setErrorMsg?: (msg: string) => void;
	additinoalInfo?: React.ReactNode;
	onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
	color?: string; //color use for border when useAltHoverStyle
	useAltHoverStyle?: boolean; //transparent background, custom border color, no shadow
	className?: string;
	startAdornment?: React.ReactNode;
}

function CustomTextInput(props: CustomTextInputProps): JSX.Element {
	const [showPassword, setShowPassword] = useState<boolean>(false);

	const originalInputType = props.type || "text";

	const label: React.ReactNode = (
		<div className="common-text-input-label">
			<InputLabel
				id={props.id + "-label"}
				sx={{
					color: props.disabled
						? NeutralColor.NEUTRAL_GREY_50
						: NeutralColor.NEUTRAL_GREY_500, // Change the color
					fontFamily: "Arial, Segoe UI, Roboto, sans-serif", // Change the font family
				}}
			>
				{props.label && props.label.length > 0
					? `${props.label} ${props.required ? "*" : ""}`
					: ""}
			</InputLabel>
			{props.description && (
				<SimpleTooltip
					id={props.id + "-tooltip"}
					description={props.description}
					arrow
					placement="top"
				></SimpleTooltip>
			)}
		</div>
	);
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: props.label ? "5px" : "0px",
			}}
			className={props.className}
		>
			{label}
			<TextField
				id={props.id}
				variant="outlined"
				type={
					originalInputType === "password" && showPassword
						? "text"
						: originalInputType
				}
				value={props.value}
				onChange={(e) => {
					let value = e.target.value;
					props.setValue(value);
					if (props.setErrorMsg) {
						props.setErrorMsg("");
					}
				}}
				onKeyDown={props.onKeyDown}
				InputProps={{
					endAdornment:
						originalInputType === "password" ? (
							<InputAdornment position="end">
								<IconButton
									aria-label="toggle password visibility"
									tabIndex={-1}
									onClick={() => {
										setShowPassword(!showPassword);
									}}
									sx={{ color: NeutralColor.NEUTRAL_GREY_200 }}
									edge="end"
								>
									{showPassword ? <VisibilityOff /> : <Visibility />}
								</IconButton>
							</InputAdornment>
						) : null,
					startAdornment: props.startAdornment,
				}}
				multiline={props.isTextArea}
				rows={props.isTextArea ? 5 : 1}
				placeholder={props.placeholder}
				required={props.required}
				disabled={props.disabled}
				sx={{
					"& .MuiOutlinedInput-root": {
						color: props.useAltHoverStyle ? props.color : undefined,
						"& textarea": { padding: "0" },
						"& fieldset": {
							borderColor: props.errorMsg
								? SemanticColor.ERROR_MEDIUM
								: NeutralColor.NEUTRAL_GREY_200, // Set border color
							borderRadius: "8px",
						},
						"&:not(.Mui-disabled):not(.Mui-focused):hover fieldset": {
							borderColor: props.useAltHoverStyle ? props.color : undefined, // Change to your desired color on hover
						},
						"&.Mui-focused fieldset": {
							boxShadow: props.useAltHoverStyle ? "none" : undefined,
						},
						"&.Mui-disabled fieldset": {
							borderColor: NeutralColor.NEUTRAL_GREY_50,
						},
					},
					"& .MuiInputBase-root.MuiOutlinedInput-root.Mui-focused ": {
						backgroundColor: props.useAltHoverStyle
							? "transparent"
							: PrimaryColor.PRIMARY_50, // Change background color only when focused
						borderRadius: "8px",
					},
				}}
			/>
			{props.errorMsg && props.errorMsg.length > 0 && (
				<div
					style={{
						display: "flex",
						gap: "5px",
						color: SemanticColor.ERROR,
						alignItems: "center",
					}}
				>
					<AlertIcon></AlertIcon>
					{props.errorMsg}
				</div>
			)}
			{props.additinoalInfo}
		</div>
	);
}

interface CustomNumberInputProps {
	id: string;
	label?: string;
	description?: string;
	placeholder?: string;
	value: number;
	setValue: Dispatch<SetStateAction<number>>;
	required?: boolean;
	disabled?: boolean;
	errorMsg?: string;
	setErrorMsg?: (msg: string) => void;

	maxDigit?: number;
	maxValue?: number;
	minValue?: number;
	className?: string;
	smallSize?: boolean;
	endAdornment?: React.ReactNode;
	startAdornment?: React.ReactNode;
	currency?: string;
}

function CustomNumberInput(props: CustomNumberInputProps): JSX.Element {
	const label: React.ReactNode = (
		<div className="common-text-input-label">
			<InputLabel
				id={props.id + "-label"}
				sx={{
					color: props.disabled
						? NeutralColor.NEUTRAL_GREY_50
						: NeutralColor.NEUTRAL_GREY_500, // Change the color
					fontFamily: "Arial, Segoe UI, Roboto, sans-serif", // Change the font family
				}}
			>
				{props.label && props.label.length > 0
					? `${props.label} ${props.required ? "*" : ""}`
					: ""}
			</InputLabel>
			{props.description && (
				<SimpleTooltip
					id={props.id + "-tooltip"}
					description={props.description}
					arrow
					placement="top"
				></SimpleTooltip>
			)}
		</div>
	);
	const maxDigit = props.maxDigit || 9;
	const MIN_VALUE = props.minValue || 0;
	const MAX_VALUE = props.maxValue ?? 10 ** maxDigit - 1;
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: props.label ? "5px" : "0px",
			}}
			className={props.className}
		>
			{label}
			<TextField
				id={props.id}
				variant="outlined"
				type="number"
				value={String(props.value)}
				onChange={(e) => {
					let value = e.target.value;
					value = value.replace(/^0+/, "") || "0"; // Ensure at least '0' is returned
					const number = Number(value);
					if (!isNaN(number) && MIN_VALUE <= number && number <= MAX_VALUE) {
						props.setValue(Math.round(number * 100) / 100);
					}
					if (props.setErrorMsg) {
						props.setErrorMsg("");
					}
				}}
				slotProps={{
					input: {
						inputProps: {
							min: MIN_VALUE, // Set minimum value
							max: MAX_VALUE, // Set maximum value
						},
						endAdornment: props.endAdornment,
					},
				}}
				placeholder={props.placeholder}
				required={props.required}
				disabled={props.disabled}
				sx={{
					"& .MuiOutlinedInput-root": {
						borderRadius: "8px", // Change the border-radius here
						height: props.smallSize ? "32px" : "40px",
						"& fieldset": {
							borderColor: props.errorMsg
								? SemanticColor.ERROR_MEDIUM
								: NeutralColor.NEUTRAL_GREY_200, // Set border color
							borderRadius: "8px",
							borderWidth: "1.5px",
						},
						"&.Mui-focused fieldset": {
							boxShadow: "0px 0px 6px #2945A940",
						},
						"&.Mui-focused": {
							backgroundColor: PrimaryColor.PRIMARY_50,
						},
					},
					"&:hover fieldset": {
						borderColor: PrimaryColor.PRIMARY_800, // Change to your desired color on hover
					},
					"& .MuiInputBase-input": {
						padding: "8px 14px", // Change the padding as needed
					},
					// "& .MuiOutlinedInput-root.Mui-focused .MuiInputBase-input": {
					// 	backgroundColor: PrimaryColor.PRIMARY_50, // Change background color only when focused
					// },
				}}
			/>
			{props.errorMsg && props.errorMsg.length > 0 && (
				<div
					style={{
						display: "flex",
						gap: "5px",
						color: SemanticColor.ERROR,
						alignItems: "center",
					}}
				>
					<AlertIcon></AlertIcon>
					{props.errorMsg}
				</div>
			)}
		</div>
	);
}

function CustomPriceInput(props: CustomNumberInputProps): JSX.Element {
	const label: React.ReactNode = (
		<div className="common-text-input-label">
			<InputLabel
				id={props.id + "-label"}
				sx={{
					color: props.disabled
						? NeutralColor.NEUTRAL_GREY_50
						: NeutralColor.NEUTRAL_GREY_500, // Change the color
					fontFamily: "Arial, Segoe UI, Roboto, sans-serif", // Change the font family
				}}
			>
				{props.label && props.label.length > 0
					? `${props.label} ${props.required ? "*" : ""}`
					: ""}
			</InputLabel>
			{props.description && (
				<SimpleTooltip
					id={props.id + "-tooltip"}
					description={props.description}
					arrow
					placement="top"
				></SimpleTooltip>
			)}
		</div>
	);
	const maxDigit = props.maxDigit || 9;
	const MIN_VALUE = 0;
	const MAX_VALUE = props.maxValue ?? 10 ** maxDigit - 1;
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: props.label ? "5px" : "0px",
			}}
		>
			{label}
			<TextField
				id={props.id}
				variant="outlined"
				type="number"
				value={String(props.value)}
				onChange={(e) => {
					let value = e.target.value;

					// Remove leading zeros, but allow "0" as a fallback
					value = value.replace(/^0+(?=\d)/, "") || "0";

					// Allow only numbers with optional dot and max 2 decimal digits
					if (!/^\d*\.?\d{0,2}$/.test(value)) return;

					const number = Number(value);
					if (!isNaN(number) && MIN_VALUE <= number && number <= MAX_VALUE) {
						props.setValue(number);
					}

					if (props.setErrorMsg) {
						props.setErrorMsg("");
					}
				}}
				onBlur={() => {
					if (typeof props.value === "number") {
						const rounded = parseFloat(props.value.toFixed(2));
						props.setValue(rounded);
					}
				}}
				placeholder={props.placeholder}
				required={props.required}
				disabled={props.disabled}
				slotProps={{
					input: {
						inputProps: {
							min: MIN_VALUE, // Set minimum value
							max: MAX_VALUE, // Set maximum value
						},
						endAdornment: (
							<InputAdornment
								position="end"
								sx={{
									pointerEvents: props.disabled ? "none" : "auto", // Prevents clicks when disabled
								}}
							>
								{props.currency}
							</InputAdornment>
						),
					},
				}}
				sx={{
					"& .MuiOutlinedInput-root": {
						borderRadius: "8px", // Change the border-radius here

						"& fieldset": {
							borderColor: props.errorMsg
								? SemanticColor.ERROR_MEDIUM
								: NeutralColor.NEUTRAL_GREY_200, // Set border color
							borderRadius: "8px",
							borderWidth: "1.5px",
						},
						"&.Mui-focused fieldset": {
							boxShadow: "0px 0px 6px #2945A940",
						},
					},
					"&:hover fieldset": {
						borderColor: PrimaryColor.PRIMARY_800, // Change to your desired color on hover
					},
					"& .MuiInputBase-input": {
						padding: "8px 14px", // Change the padding as needed
					},
					"& .MuiOutlinedInput-root.Mui-focused": {
						backgroundColor: PrimaryColor.PRIMARY_50, // Change background color only when focused
					},
				}}
			/>
			{props.errorMsg && props.errorMsg.length > 0 && (
				<div
					style={{
						display: "flex",
						gap: "5px",
						color: SemanticColor.ERROR,
						alignItems: "center",
					}}
				>
					<AlertIcon></AlertIcon>
					{props.errorMsg}
				</div>
			)}
		</div>
	);
}

interface CustomNumberInputDropdownProps extends CustomNumberInputProps {
	options: Array<{
		title: string;
		value: any;
		onClick?: () => void;
		renderValue?: string;
		disabled?: boolean;
	}>;
	selectId: string;
	selectValue: any;
	setSelectValue: Dispatch<SetStateAction<any>>;
}
function CustomNumberInputDropdown(
	props: CustomNumberInputDropdownProps
): JSX.Element {
	const label: React.ReactNode = (
		<div className="common-text-input-label">
			<InputLabel
				id={props.id + "-label"}
				sx={{
					color: props.disabled
						? NeutralColor.NEUTRAL_GREY_50
						: NeutralColor.NEUTRAL_GREY_500, // Change the color
					fontFamily: "Arial, Segoe UI, Roboto, sans-serif", // Change the font family
				}}
			>
				{props.label}
			</InputLabel>
			{props.description && (
				<SimpleTooltip
					id={props.id + "-tooltip"}
					description={props.description}
					arrow
					placement="top"
				></SimpleTooltip>
			)}
		</div>
	);
	const maxDigit = props.maxDigit || 9;
	const MIN_VALUE = props.minValue || 0;
	const MAX_VALUE = props.maxValue ?? 10 ** maxDigit - 1;
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: props.label ? "5px" : "0px",
			}}
			className={props.className}
		>
			{label}
			<TextField
				id={props.id}
				variant="outlined"
				type="number"
				value={String(props.value)}
				onChange={(e) => {
					let value = e.target.value;
					value = value.replace(/^0+/, "") || "0"; // Ensure at least '0' is returned
					const number = Number(value);
					if (!isNaN(number) && MIN_VALUE <= number && number <= MAX_VALUE) {
						props.setValue(Math.round(number * 100) / 100);
					}
					if (props.setErrorMsg) {
						props.setErrorMsg("");
					}
				}}
				slotProps={{
					input: {
						inputProps: {
							min: MIN_VALUE, // Set minimum value
							max: MAX_VALUE, // Set maximum value
						},
						endAdornment: (
							<InputAdornment position="end" style={{ width: "66px" }}>
								<Select
									id={props.selectId}
									value={props.selectValue}
									disabled={props.disabled}
									onChange={(event: any) => {
										props.setSelectValue(event.target.value);
									}}
									IconComponent={KeyboardArrowDownIcon} // Toggle icon based on open state
									renderValue={(selected) => {
										// For single selection
										const selectedOption = props.options.find(
											(option) => option.value === selected
										);
										return (
											selectedOption?.renderValue || selectedOption?.title || ""
										);
									}}
									sx={{
										height: props.smallSize ? "32px" : "40px",
										".MuiSelect-select": {
											padding: props.smallSize ? "4px 14px" : "8px 16px", // Adjust padding for the input area (where selected value is shown)
											backgroundColor: "transparent",
											borderRadius: "0px 8px 8px 0px", // Change the border radius
										},

										"&:not(.Mui-disabled):hover fieldset": {
											border: "none",
											borderLeft: "1px solid #AFAFAF",
											borderWidth: "1px",
										},
										"& fieldset": {
											backgroundColor: "transparent",
											boxShadow: "none",
										},
										"&.Mui-focused fieldset": {
											backgroundColor: "transparent",
											boxShadow: "none !important",
										},
										"&:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
											border: "none",
											borderLeft: "1px solid #AFAFAF",
											borderRadius: "0px 8px 8px 0px",
										},
										"&.MuiOutlinedInput-root": {
											width: "64px",
										},
										"&.MuiOutlinedInput-root.Mui-focused": {
											backgroundColor: "transparent",
										},
										borderRadius: "0px 8px 8px 0px", // Change the border radius
									}}
									MenuProps={{
										PaperProps: {
											sx: {
												marginTop: "2px",
												border: "1.5px solid" + PrimaryColor.PRIMARY_800,
												borderRadius: "8px",
												backgroundColor: "white",
												boxShadow: "0px 0px 8px #2945A940",
											},
										},
									}}
								>
									{props.options.map((entry, index) => (
										<MenuItem
											key={index}
											value={entry.value}
											onClick={entry.onClick}
											sx={{
												padding: "8px 16px",
												"&:hover": {
													backgroundColor: BackgroundColor.LIGHT_BLUE,
												},
											}}
										>
											{entry.title}
										</MenuItem>
									))}
								</Select>
							</InputAdornment>
						),
					},
				}}
				placeholder={props.placeholder}
				required={props.required}
				disabled={props.disabled}
				sx={{
					"& .MuiOutlinedInput-root": {
						borderRadius: "8px", // Change the border-radius here
						height: props.smallSize ? "32px" : "40px",
						"& fieldset": {
							borderColor: props.errorMsg
								? SemanticColor.ERROR_MEDIUM
								: NeutralColor.NEUTRAL_GREY_200, // Set border color
							borderRadius: "8px",
							borderWidth: "1.5px",
						},
						"&.Mui-focused fieldset": {
							boxShadow: "0px 0px 6px #2945A940",
						},
						"&.Mui-focused": {
							backgroundColor: PrimaryColor.PRIMARY_50,
						},
					},
					"&:hover fieldset": {
						borderColor: PrimaryColor.PRIMARY_800, // Change to your desired color on hover
					},
					"& .MuiInputBase-input": {
						padding: "8px 14px", // Change the padding as needed
					},
					"& .MuiInputBase-root": {
						padding: "0px", // Change the padding as needed
					},
					// "& .MuiOutlinedInput-root.Mui-focused .MuiInputBase-input": {
					// 	backgroundColor: PrimaryColor.PRIMARY_50, // Change background color only when focused
					// },
				}}
			/>
			{props.errorMsg && props.errorMsg.length > 0 && (
				<div
					style={{
						display: "flex",
						gap: "5px",
						color: SemanticColor.ERROR,
						alignItems: "center",
					}}
				>
					<AlertIcon></AlertIcon>
					{props.errorMsg}
				</div>
			)}
		</div>
	);
}

interface DropdownProps {
	id: string;
	label?: string;
	description?: string;
	options: Array<{
		title: string;
		value: any;
		onClick?: () => void;
		renderValue?: string;
		disabled?: boolean;
	}>;
	value: any;
	setValue: (value: any) => void;
	className?: string;
	labelClassName?: string;
	labelDescriptionColor?: string;
	required?: boolean;
	noBorder?: boolean;
	displayEmpty?: boolean;
	disabled?: boolean;
	hasError?: boolean;
	backgroundColor?: string;
	color?: string;
	smallSize?: boolean;
	multiple?: boolean;
	placeholder?: string;
}

function CustomDropdown(props: DropdownProps): JSX.Element {
	const [menuOpen, setMenuOpen] = useState(false);

	const handleChange = (event: any) => {
		props.setValue(event.target.value);
	};

	return (
		<div
			className={`common-dropdown-container ${props.className || ""}`}
			style={{ gap: props.smallSize ? "0px" : "5px" }}
		>
			<div style={{ padding: "0px 16px" }}>
				<InputLabel
					id={props.id + "-label"}
					className={props.labelClassName}
					sx={{
						color: props.disabled
							? NeutralColor.NEUTRAL_GREY_50
							: NeutralColor.NEUTRAL_GREY_500, // Change the color
						fontFamily: "Arial, Segoe UI, Roboto, sans-serif", // Change the font family
					}}
				>
					{props.label}
				</InputLabel>
			</div>

			<Select
				labelId={props.id + "-label"}
				id={props.id}
				value={props.value}
				disabled={props.disabled}
				onChange={handleChange}
				onOpen={() => {
					setMenuOpen(true);
				}}
				onClose={() => setMenuOpen(false)}
				IconComponent={KeyboardArrowDownIcon} // Toggle icon based on open state
				renderValue={(selected) => {
					if (props.placeholder && selected.length === 0) {
						return <em className="grey-text">{props.placeholder}</em>;
					}
					const isMultiple = props.multiple;
					if (isMultiple && Array.isArray(selected)) {
						// For multiple selection, map selected values to their titles
						const selectedTitles = selected
							.map((value) => {
								const match = props.options.find(
									(option) => option.value === value
								);
								return match?.renderValue || match?.title || "";
							})
							.join(", ");
						return selectedTitles;
					} else {
						// For single selection
						const selectedOption = props.options.find(
							(option) => option.value === selected
						);
						return selectedOption?.renderValue || selectedOption?.title || "";
					}
				}}
				sx={{
					height: props.smallSize ? "32px" : "40px",
					color: props.color,
					".MuiSelect-select": {
						padding: props.smallSize ? "4px 14px" : "8px 16px", // Adjust padding for the input area (where selected value is shown)
						backgroundColor: props.backgroundColor || "white",
						borderRadius: "8px", // Change the border radius
					},
					"&:not(.Mui-disabled):hover fieldset": {
						border: props.noBorder ? "none" : null,
						borderColor: PrimaryColor.PRIMARY_800, // Change to your desired color on hover
						borderWidth: "1.5px",
					},
					"&:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
						border: props.noBorder ? "none" : null,
						borderColor: props.hasError
							? SemanticColor.ERROR
							: NeutralColor.NEUTRAL_GREY_200, // Set border color
					},
					"&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
						borderColor: NeutralColor.NEUTRAL_GREY_50,
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: PrimaryColor.PRIMARY_800, // Set border color
						borderWidth: "1.5px",
					},
					".MuiSvgIcon-root": {
						color: props.color,
					},
					borderRadius: "8px", // Change the border radius
				}}
				MenuProps={{
					PaperProps: {
						sx: {
							marginTop: "2px",
							border: "1.5px solid" + PrimaryColor.PRIMARY_800,
							borderRadius: "8px",
							backgroundColor: "white",
							boxShadow: "0px 0px 8px #2945A940",
						},
					},
				}}
				displayEmpty={props.displayEmpty}
				multiple={props.multiple}
			>
				{props.options.map((entry, index) => (
					<MenuItem
						key={index}
						value={entry.value}
						onClick={entry.onClick}
						sx={{
							padding: "8px 16px",
							"&:hover": { backgroundColor: BackgroundColor.LIGHT_BLUE },
						}}
					>
						{props.multiple && (
							<CustomCheckbox
								label={""}
								checked={props.value.includes(entry.value)}
								setChecked={() => {}}
							/>
						)}
						{entry.title}
					</MenuItem>
				))}
			</Select>
		</div>
	);
}

function RowDropdown(props: DropdownProps): JSX.Element {
	// const [icon, setIcon] = useState<any>(KeyboardArrowDownIcon); // State to manage whether the select is open

	const handleChange = (event: any) => {
		props.setValue(event.target.value);
	};
	// const handleOpen = () => {
	// 	setIcon(KeyboardArrowUpIcon);
	// };

	// const handleClose = () => {
	// 	setIcon(KeyboardArrowDownIcon);
	// };

	return (
		<div className={`common-row-dropdown-container ${props.className || ""}`}>
			<div className={props.labelClassName}>
				<div>{props.label}</div>
				{props.description && (
					<div
						className={`typography-caption`}
						style={{
							color:
								props.labelDescriptionColor || NeutralColor.NEUTRAL_GREY_600,
						}}
					>
						{props.description}
					</div>
				)}
			</div>

			<Select
				labelId={props.id + "-label"}
				id={props.id}
				value={props.value}
				onChange={handleChange}
				// onOpen={handleOpen}
				// onClose={handleClose}
				IconComponent={KeyboardArrowDownIcon} // Toggle icon based on open state
				renderValue={(selected) => {
					// Return the label corresponding to the selected value
					const selectedOption = props.options.find(
						(option) => option.value === selected
					);
					return selectedOption
						? selectedOption.renderValue || selectedOption.title
						: "";
				}}
				sx={{
					".MuiSelect-select": {
						padding: "8px 16px", // Adjust padding for the input area (where selected value is shown)
						// backgroundColor: "white",
						borderRadius: "8px", // Change the border radius
					},
					"& .MuiOutlinedInput-notchedOutline": {
						border: props.noBorder ? "none" : null,
						borderColor: props.hasError
							? SemanticColor.ERROR
							: NeutralColor.NEUTRAL_GREY_200, // Set border color
					},
					"&:not(.Mui-disabled):not(.Mui-focused):hover fieldset": {
						borderColor: PrimaryColor.PRIMARY_800, // Change to your desired color on hover
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: PrimaryColor.PRIMARY_800, // Set border color
						borderWidth: "1px",
					},
					"&.MuiInputBase-root": {
						backgroundColor: props.backgroundColor,
					},

					marginTop: props.label ? 0 : "23px",
					borderRadius: "8px", // Change the border radius
					overflow: "hidden",
				}}
				MenuProps={{
					PaperProps: {
						sx: {
							border: "1px solid" + PrimaryColor.PRIMARY_800,
							marginTop: "2px",
							borderRadius: "8px",
							overflow: "hidden",
							boxShadow: "0px 0px 8px #2945A940",
						},
					},
				}}
				displayEmpty={props.displayEmpty}
			>
				{props.options.map((entry, index) => {
					return (
						<MenuItem key={index} value={entry.value} onClick={entry.onClick}>
							{entry.title}
						</MenuItem>
					);
				})}
			</Select>
		</div>
	);
}

interface CheckboxProps {
	label: string;
	description?: string;
	className?: string;
	labelClassName?: string;
	disabled?: boolean;
	checked: boolean;
	setChecked: Dispatch<SetStateAction<boolean>>;
}

function CustomCheckbox(props: CheckboxProps): JSX.Element {
	const labelClassName = props.labelClassName || "typography-body-02";
	return (
		<label
			className={props.className}
			style={{
				display: "flex",
				alignItems: "center",
				cursor: props.disabled ? "not-allowed" : "pointer",
			}}
		>
			<input
				type={"checkbox"}
				checked={props.checked}
				onChange={(e) => {
					props.setChecked(!props.checked);
				}}
				style={{ display: "none" }}
				disabled={props.disabled}
			/>
			<div
				className={`custom-checkbox ${
					props.disabled
						? "custom-checkbox-disabled"
						: props.checked
						? "custom-checkbox-checked"
						: ""
				}`}
			></div>
			<div
				className={`${labelClassName} ${
					props.disabled ? "disabled-label" : ""
				}`}
			>
				{props.label}
			</div>

			{props.description && (
				<div>
					<small className="input-description">{props.description}</small>
				</div>
			)}
		</label>
	);
}

interface SearchBarProps {
	id: string;
	placeholder: string;
	searchButtonText: string;
	searchText: string;
	setSearchText: Dispatch<SetStateAction<string>>;
	onSearch: (searchText: string) => void;
	disabled?: boolean;
}

function CustomSearchBar(props: SearchBarProps): JSX.Element {
	const { searchText, setSearchText } = props;
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(event.target.value);
	};
	const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			props.onSearch(searchText);
		}
	};

	return (
		<TextField
			id={props.id}
			variant="outlined"
			type="text"
			value={searchText}
			onChange={handleChange}
			onKeyUp={handleKeyPress}
			inputProps={{
				maxLength: 100,
			}}
			InputProps={{
				endAdornment: (
					<InputAdornment
						position="end"
						sx={{
							cursor: props.disabled ? "not-allowed" : "pointer",
							pointerEvents: props.disabled ? "none" : "auto", // Prevents clicks when disabled
						}}
						onClick={(event) => {
							if (!props.disabled && props.onSearch) {
								props.onSearch(searchText);
							}
						}}
					>
						<SearchIcon
							strokeColor={
								props.disabled
									? PrimaryColor.PRIMARY_50
									: PrimaryColor.PRIMARY_800
							}
						/>
					</InputAdornment>
				),
			}}
			placeholder={props.disabled ? "" : props.placeholder}
			sx={{
				"& .MuiOutlinedInput-root": {
					"& fieldset": {
						borderColor: "transparent", // Set border color
						borderRadius: "8px",
						borderWidth: "1px",
					},
					"&:not(.Mui-disabled):hover fieldset": {
						borderColor: PrimaryColor.PRIMARY_800, // Change to your desired color on hover
					},
					"& .MuiInputBase-input": {
						padding: "8px 14px", // Adjust padding inside the input element
					},
					height: "40px",
				},
			}}
		/>
	);
}

interface AutoCompleteSearchBarProps {
	id: string;
	options: Array<any>;
	selectedOption: any | null;
	setSelectedOption: Dispatch<SetStateAction<any>>;
	setErrorMsg: Dispatch<SetStateAction<string>>;
	placeholder: string;
	searchButtonText: string;
	searchText: string;
	setSearchText: Dispatch<SetStateAction<string>>;
	onSearch: (searchText: string) => void;
	disabled?: boolean;
	loading?: boolean;
}

function AutoCompleteSearchBar(props: AutoCompleteSearchBarProps): JSX.Element {
	const { searchText, setSearchText } = props;
	const [open, setOpen] = useState<boolean>(false);
	const handleChange = (
		event: React.SyntheticEvent<Element, Event>,
		newValue: any
	) => {
		if (newValue !== null) {
			props.setSelectedOption(newValue);
		} else {
			props.setSelectedOption(null);
		}
		if (props.selectedOption) {
			props.setErrorMsg("");
		}
	};
	const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			props.onSearch(searchText);
		}
	};
	const [popperHeight, setPopperHeight] = useState<number>(0);
	const popperRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (open && popperRef.current) {
			setPopperHeight(popperRef.current.offsetHeight);
		} else {
			setPopperHeight(0);
		}
	}, [open, searchText]);

	return (
		<div
			style={{
				boxShadow: open ? "0px 0px 8px #2945A940" : "none",
				height: open ? `calc(48px + ${popperHeight}px)` : "40px",
				borderRadius: "8px",
				backgroundColor: "white",
			}}
		>
			<Autocomplete
				disablePortal={true}
				id={props.id}
				onOpen={(e) => setOpen(true)}
				onClose={(e) => setOpen(false)}
				options={props.options || []}
				value={props.selectedOption || null}
				loading={props.loading}
				popupIcon={
					<InputAdornment
						position="end"
						sx={{
							cursor: props.disabled ? "not-allowed" : "pointer",
							pointerEvents: props.disabled ? "none" : "auto", // Prevents clicks when disabled
						}}
						onClick={(event) => {
							if (!props.disabled && props.onSearch) {
								props.onSearch(searchText);
							}
						}}
					>
						<SearchIcon
							strokeColor={
								props.disabled
									? PrimaryColor.PRIMARY_50
									: PrimaryColor.PRIMARY_800
							}
						/>
					</InputAdornment>
				} // Toggle icon based on open state
				onChange={handleChange}
				onInputChange={(e, newValue) => {
					setSearchText(newValue);
					if (props.setErrorMsg) {
						props.setErrorMsg("");
					}
				}}
				sx={{
					backgroundColor: BackgroundColor.LIGHT_GREY,
					borderRadius: open ? "8px 8px 0px 0px" : "8px",
					boxShadow: "none",
					"& .MuiAutocomplete-popupIndicator": { transform: "none" },
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						variant="outlined"
						onKeyUp={handleKeyPress}
						label=""
						inputProps={{
							maxLength: 100,
							...params.inputProps,
						}}
						placeholder={props.disabled ? "" : props.placeholder}
						sx={{
							"& .MuiOutlinedInput-root": {
								"& fieldset": {
									borderColor: "transparent", // Set border color
									borderRadius: open ? "8px 8px 0px 0px" : "8px",
									borderWidth: "1px",
								},
								"&:not(.Mui-disabled):hover fieldset": {
									borderColor: PrimaryColor.PRIMARY_800, // Change to your desired color on hover
								},
								"&.Mui-focused fieldset": {
									border: "none",
									boxShadow: "none",
								},
								height: "40px",
							},
						}}
						disabled={props.disabled}
					/>
				)}
				getOptionLabel={(option) => {
					return option
						? `${option.userName} - ${option.firstName} ${option.lastName}`
						: option;
				}}
				isOptionEqualToValue={(option, value) => option === value} // Comparison for selected value
				slotProps={{
					paper: {
						sx: {
							marginTop: "0px",
							border: "none",
							borderRadius: "0px 0px 8px 8px",
							backgroundColor: "white",
							boxShadow: "none",
						},
					},
					popper: {
						ref: popperRef,
					},
				}}
				disabled={props.disabled}
			/>
		</div>
	);
}

interface LabeledSwitchProps {
	label: string;
	labelDescription?: string;
	labelTypographyClass?: string;
	uncheckedLabel: string;
	checkedLabel: string;
	checked: boolean;
	setChecked: Dispatch<SetStateAction<boolean>>;
}

function LabeledSwitch(props: LabeledSwitchProps): JSX.Element {
	const label: React.ReactNode = (
		<div>
			<label className={`${props.labelTypographyClass}`}>{props.label}</label>
			<div
				style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
				className="typography-caption"
			>
				{props.labelDescription}
			</div>
		</div>
	);
	return (
		<div
			style={{
				display: "flex",
				columnGap: "20px",
				alignItems: "center",
				flexWrap: "wrap",
			}}
		>
			{label}

			<Box sx={{ position: "relative", display: "inline-block" }}>
				<CustomSwitch
					checked={props.checked}
					onChange={(e) => {
						props.setChecked(!props.checked);
					}}
					color="primary"
				/>
				<Box
					sx={{
						position: "absolute",
						left: "50%",
						width: "100%",
						display: "flex",
						justifyContent: props.checked ? "left" : "right",
						top: "50%",
						transform: "translate(-50%, -50%)",
						pointerEvents: "none", // Prevent interaction with text
					}}
				>
					<Typography
						sx={{
							color: props.checked ? "white" : PrimaryColor.PRIMARY_800,
							fontSize: "12px",
							margin: "7px",
						}}
					>
						{props.checked ? props.checkedLabel : props.uncheckedLabel}
					</Typography>
				</Box>
			</Box>
		</div>
	);
}

interface CommonSwitchProps {
	id: string;
	label?: string;
	labelTypographyClass?: string;
	checked: boolean;
	setChecked: (checked: boolean) => void;
}

function CommonSwitch(props: CommonSwitchProps): JSX.Element {
	const label: React.ReactNode = props.label ? (
		<label className={`${props.labelTypographyClass}`}>{props.label}</label>
	) : null;
	return (
		<div style={{ display: "flex", columnGap: "20px", alignItems: "center" }}>
			{label}
			<Box sx={{ position: "relative", display: "inline-block" }}>
				<CustomSwitch
					id={props.id}
					checked={props.checked}
					onChange={(e) => {
						props.setChecked(!props.checked);
					}}
					color="primary"
				/>
			</Box>
		</div>
	);
}

interface RadioGroupProps {
	options: Array<{
		name: string;
		description?: string;
		value: any;
		children?: React.ReactNode;
		key: string;
	}>;
	selectedOption: any;
	onChange: (event: any) => void;
	className?: string;
}
function CardRadioGroup(props: RadioGroupProps): JSX.Element {
	//hide default radio, customize look
	const radios: Array<React.ReactNode> = props.options.map((option, index) => {
		const checked: boolean = props.selectedOption === option.value;
		return (
			<label key={option.key} style={{ cursor: "pointer", height: "100%" }}>
				<input
					type="radio"
					name={option.name}
					value={option.value}
					checked={checked}
					onChange={props.onChange}
					style={{ display: "none" }}
				/>
				<div
					className={`card-radio-wrapper ${
						checked ? "card-radio-wrapper-checked" : ""
					}`}
					tabIndex={0}
				>
					<div
						className={`card-radio-button ${
							checked ? "card-radio-button-checked" : ""
						}`}
					>
						<div className="typography-heading-03">{option.name}</div>
						{option.description && (
							<div
								className="typography-body-02"
								style={{ textAlign: "center" }}
							>
								{option.description}
							</div>
						)}
					</div>
				</div>
			</label>
		);
	});
	return (
		<div
			className={props.className}
			style={{
				display: "grid",
				gridAutoRows: "1fr",
				rowGap: "20px",
				alignItems: "center",
			}}
		>
			{radios}
		</div>
	);
}

function StandardRadioGroup(props: RadioGroupProps): JSX.Element {
	//hide default radio, customize look
	const radios: Array<React.ReactNode> = props.options.map((option, index) => {
		const checked: boolean = props.selectedOption === option.value;
		return (
			<label
				key={option.key}
				style={{
					display: "flex",
					cursor: "pointer",
					columnGap: "5px",
					alignItems: "center",
				}}
			>
				<input
					type="radio"
					name={option.name}
					value={option.value}
					checked={checked}
					onChange={props.onChange}
					style={{ display: "none" }}
				/>

				<div
					className={`custom-radio ${checked ? "custom-radio-checked" : ""}`}
				></div>
				<div className="typography-body-02">{option.name}</div>
			</label>
		);
	});
	return (
		<div
			className={props.className}
			style={{
				display: "flex",
				justifyContent: "space-between",
				rowGap: "20px",
				alignItems: "center",
				flexWrap: "wrap",
			}}
		>
			{radios}
		</div>
	);
}

function CustomRadioGroup(props: RadioGroupProps): JSX.Element {
	//hide default radio, customize look
	const radios: Array<JSX.Element> = props.options.map((option) => {
		const checked: boolean = props.selectedOption === option.value;

		return (
			<label
				key={option.key}
				style={{ cursor: "pointer", height: "100%", width: "100%" }}
			>
				<input
					type="radio"
					name={option.name}
					value={option.value}
					checked={checked}
					onChange={props.onChange}
					style={{ display: "none" }}
				/>
				{option.children}
			</label>
		);
	});
	return <div className={props.className}>{radios}</div>;
}

export {
	UserInfoTextInput,
	CustomDropdown,
	CustomCheckbox,
	CustomSearchBar,
	CustomTextInput,
	CustomNumberInput,
	CustomPriceInput,
	CustomNumberInputDropdown,
	LabeledSwitch,
	CardRadioGroup,
	StandardRadioGroup,
	CommonSwitch,
	CustomRadioGroup,
	RowDropdown,
	AutoCompleteSearchBar,
};
